<template>
  <div class="store-report poppins">
    <!-- Page Title -->
    <div class="store-report__page-header">
      <h2 class="store-report__page-title">{{ $t("ai_demand_forecast") }}</h2>
    </div>

    <v-row no-gutters>
      <!-- ── Left Panel: Filters & History ────────────────────── -->
      <v-col cols="12" md="3">
        <div class="store-report__filter-card">
          <!-- Tabs Header -->
          <v-tabs
            v-model="activeTab"
            background-color="transparent"
            color="primary"
            grow
            hide-slider
            class="store-report__tabs"
            height="48"
          >
            <v-tab class="store-report__tab">
              <v-icon left size="18">mdi-filter-variant</v-icon>
              {{ $t("parameters") }}
            </v-tab>
            <v-tab class="store-report__tab">
              <v-icon left size="18">mdi-history</v-icon>
              {{ $t("history") }}
              <v-chip
                v-if="reportStructure.history && reportStructure.history.length"
                x-small
                color="primary"
                text-color="white"
                class="ml-2"
              >
                {{ reportStructure.history.length }}
              </v-chip>
            </v-tab>
          </v-tabs>

          <v-tabs-items
            v-model="activeTab"
            style="background-color: transparent"
            class="mt-4"
          >
            <!-- ── Filters Tab ──────────────────────────────── -->
            <v-tab-item>
              <!-- Dynamic Filters -->
              <div
                v-for="filter in reportStructure.filters"
                :key="filter.name"
                class="store-report__field"
              >
                <label class="store-report__label">
                  <img
                    v-if="filter.icon"
                    :src="filter.icon"
                    :alt="getFilterTitle(filter)"
                    class="store-report__label-icon"
                  />
                  {{ getFilterTitle(filter) }}
                  <v-tooltip
                    right
                    max-width="300"
                    v-if="filterTooltipMap[filter.name]"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        v-bind="attrs"
                        v-on="on"
                        size="16"
                        color="#94a3b8"
                        class="ml-1"
                        style="cursor: help"
                      >
                        mdi-information-outline
                      </v-icon>
                    </template>
                    <span>{{ $t(filterTooltipMap[filter.name]) }}</span>
                  </v-tooltip>
                </label>

                <!-- select2Single -->
                <v-select
                  v-if="filter.type === 'select2Single'"
                  v-model="filterValues[filter.name]"
                  :items="filter.values"
                  item-text="text"
                  item-value="index"
                  dense
                  outlined
                  :placeholder="
                    $t('select_field', { field: getFilterTitle(filter) })
                  "
                  hide-details
                  class="store-report__input"
                  :disabled="limit_finish"
                />

                <!-- inputText -->
                <template v-else-if="filter.type === 'inputText'">
                  <v-autocomplete
                    v-if="filter.title.toLowerCase().includes('sku number')"
                    v-model="filterValues[filter.name]"
                    :item-text="skuItemText"
                    item-value="systemCode"
                    :items="items"
                    :loading="isLoading"
                    :search-input.sync="search"
                    hide-no-data
                    hide-selected
                    dense
                    outlined
                    clearable
                    :placeholder="
                      $t('enter_field', { field: getFilterTitle(filter) })
                    "
                    hide-details
                    class="store-report__input"
                    @keyup="debounceSearch"
                    :disabled="limit_finish"
                  ></v-autocomplete>
                  <!--                  <v-text-field-->
                  <!--                    v-if="filter.title.toLowerCase().includes('sku number')"-->
                  <!--                    v-model="filterValues[filter.name]"-->
                  <!--                    dense-->
                  <!--                    outlined-->
                  <!--                    clearable-->
                  <!--                    :placeholder="`Enter ${filter.title}`"-->
                  <!--                    hide-details-->
                  <!--                    class="store-report__input"-->
                  <!--                    @keyup.enter="onAnalyze"-->
                  <!--                  />-->
                  <v-text-field
                    v-else
                    v-model="filterValues[filter.name]"
                    dense
                    outlined
                    clearable
                    :placeholder="
                      $t('enter_field', { field: getFilterTitle(filter) })
                    "
                    hide-details
                    class="store-report__input"
                    @keyup.enter="onAnalyze"
                    :disabled="limit_finish"
                  />
                </template>
              </div>

              <template v-if="reportStructure.filters?.length">
                <!-- Actions -->
                <div class="store-report__actions">
                  <v-btn
                    color="primary"
                    depressed
                    block
                    class="store-report__analyze-btn"
                    @click="onAnalyze"
                    :disabled="limit_finish"
                  >
                    <v-icon left size="18">mdi-chart-line</v-icon>
                    {{ $t("analyze") }}
                  </v-btn>
                </div>

                <div class="store-report__justification mt-6">
                  <div class="store-report__justification-header">
                    <v-icon size="18" color="#7e04b7" class="mr-1"
                      >mdi-information-outline</v-icon
                    >
                    <span>{{ $t("credits") }}</span>
                  </div>
                  <p
                    class="store-report__justification-text"
                    v-if="limit && !limit_finish"
                  >
                    {{
                      $t("you_have_x_searches_remaining_for_this_month", {
                        count: limit,
                      })
                    }}
                  </p>
                  <p
                    v-else-if="limit_finish"
                    class="store-report__justification-text"
                  >
                    {{
                      $t(
                        "you_have_reached_your_monthly_report_generation_limit"
                      )
                    }}
                  </p>
                </div>
              </template>
            </v-tab-item>

            <!-- ── History Tab ──────────────────────────────── -->
            <v-tab-item>
              <div
                v-if="reportStructure.history && reportStructure.history.length"
              >
                <div class="store-report__history-list">
                  <div
                    v-for="entry in reportStructure.history"
                    :key="entry.id"
                    class="store-report__history-item"
                    :class="{
                      'store-report__history-item--active':
                        activeHistoryId === entry.id,
                    }"
                    @click="applyHistory(entry)"
                  >
                    <div class="store-report__history-chips">
                      <span
                        v-for="f in entry.filters"
                        :key="f.name"
                        class="store-report__history-chip"
                      >
                        {{ f.value_label }}
                      </span>
                    </div>
                    <span class="store-report__history-date">
                      {{ entry.created_at }}
                    </span>
                  </div>
                </div>
              </div>
              <div v-else class="py-10 text-center">
                <v-icon size="40" color="#e2e8f0" class="mb-3"
                  >mdi-history</v-icon
                >
                <div
                  class="store-report__empty-desc mx-auto"
                  style="font-size: 13px; color: #94a3b8"
                >
                  {{ $t("no_history_available_yet") }}
                </div>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </div>
      </v-col>

      <!-- ── Right Panel: Results ─────────────────────────────── -->
      <v-col cols="12" md="9">
        <div class="store-report__results">
          <!-- Empty State -->
          <div v-if="!outputData" class="store-report__empty">
            <v-icon size="64" color="#d1d5db">mdi-chart-box-outline</v-icon>
            <h3 class="store-report__empty-title">
              {{ $t("no_results_yet") }}
            </h3>
            <p class="store-report__empty-desc">
              {{
                $t(
                  "select_your_filters_and_click_analyze_to_generate_the_sku_forecast_report"
                )
              }}
            </p>
          </div>

          <!-- Results -->
          <div v-else class="store-report__list">
            <div class="store-report__list-header">
              <span class="store-report__list-title">
                <v-icon size="20" color="#7e04b7" class="mr-1"
                  >mdi-clipboard-text-outline</v-icon
                >
                {{ $t("forecast_results") }}
              </span>

              <!-- Export -->
              <div
                v-if="reportStructure.export"
                class="store-report__summary-footer align-items-center"
              >
                <v-chip small color="primary" text-color="white" class="mx-2">
                  SKU: {{ outputData.sku }}
                </v-chip>

                <!--                <v-btn-->
                <!--                  small-->
                <!--                  depressed-->
                <!--                  rounded-->
                <!--                  outlined-->
                <!--                  color="primary"-->
                <!--                  class="store-report__download-btn mx-4"-->
                <!--                >-->
                <!--                  <v-icon left size="16">mdi-download</v-icon>-->
                <!--                  Export-->
                <!--                </v-btn>-->
              </div>
            </div>

            <!-- Summary Meta -->
            <div class="store-report__summary-inner">
              <div class="store-report__meta-row">
                <div class="store-report__meta-item">
                  <span class="store-report__meta-label">{{ $t("sku") }}</span>
                  <span class="store-report__meta-value">{{
                    outputData.sku
                  }}</span>
                </div>
                <div class="store-report__meta-item">
                  <span class="store-report__meta-label">{{
                    $t("trend")
                  }}</span>
                  <span class="store-report__meta-value">{{
                    outputData.trend
                  }}</span>
                </div>
                <div class="store-report__meta-item">
                  <span class="store-report__meta-label">{{
                    $t("stability")
                  }}</span>
                  <span class="store-report__meta-value">{{
                    outputData.stability
                  }}</span>
                </div>
                <div class="store-report__meta-item">
                  <span class="store-report__meta-label">{{
                    $t("confidence")
                  }}</span>
                  <span
                    class="store-report__meta-value"
                    :class="confidenceClass"
                  >
                    {{ outputData.confidence }}
                  </span>
                </div>
              </div>

              <!-- Justification -->
              <div
                v-if="outputData.justification"
                class="store-report__justification"
              >
                <div class="store-report__justification-header">
                  <v-icon size="18" color="#7e04b7" class="mr-1"
                    >mdi-information-outline</v-icon
                  >
                  <span>{{ $t("summary") }}</span>
                </div>
                <p class="store-report__justification-text">
                  {{ outputData.justification }}
                </p>
              </div>

              <!-- Metrics Grid -->
              <div class="store-report__metrics">
                <div
                  v-for="metric in computedMetrics"
                  :key="metric.label"
                  class="store-report__metric-card"
                >
                  <v-icon size="26" :color="metric.color" class="mb-1">{{
                    metric.icon
                  }}</v-icon>
                  <span class="store-report__metric-value">{{
                    metric.value
                  }}</span>
                  <span class="store-report__metric-label">{{
                    metric.label
                  }}</span>
                </div>
              </div>

              <!-- Export -->
              <!--              <div-->
              <!--                v-if="reportStructure.export"-->
              <!--                class="store-report__summary-footer"-->
              <!--              >-->
              <!--                <v-btn-->
              <!--                  small-->
              <!--                  depressed-->
              <!--                  color="primary"-->
              <!--                  class="store-report__download-btn"-->
              <!--                >-->
              <!--                  <v-icon left size="16">mdi-download</v-icon>-->
              <!--                  Export-->
              <!--                </v-btn>-->
              <!--              </div>-->
            </div>
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service";
import { SET_PAGE_LOADING } from "@/core/services/store/config.module";
import { getToken } from "@/core/services/jwt.service";

export default {
  name: "ReportSkuForecast",

  data: () => ({
    // ── API-driven structure ────────────────────────────────
    reportStructure: {
      properties: { title: "", description: "" },
      filters: [],
      export: false,
      history: [],
    },

    // ── Filter values keyed by filter.name ──────────────────
    filterValues: {},
    entries: [],

    // ── Filter title & tooltip overrides ────────────────────
    filterTitleMap: {
      last_order_date_range: "analysis_period_days",
      order_country_regions: "sales_region",
      lead_time_days: "lead_time_duration_days",
    },
    filterTooltipMap: {
      last_order_date_range:
        "number_of_past_days_used_to_analyze_sales_history_and_generate_the_demand_forecast",
      order_country_regions:
        "select_the_geographic_region_to_filter_demand_and_sales_data",
      lead_time_days:
        "number_of_days_needed_for_the_supplier_or_manufacturer_to_produce_and_deliver_the_products",
    },

    // ── Results state ───────────────────────────────────────
    outputData: null,
    activeHistoryId: null,
    isLoading: false,
    search: false,
    debounce: false,

    limit: null,
    limit_finish: false,

    // ── Active Tab ──────────────────────────────────────────
    activeTab: 0,
  }),

  computed: {
    items: function () {
      return this.entries;
    },
    isClient: function () {
      // return true;
      return this.$store.getters.isClient;
    },
    /** Map output fields to metric cards */
    computedMetrics() {
      if (!this.outputData) return [];

      const metricsMap = [
        {
          key: "forecast_30d",
          label: this.$t("thirty_day_forecast"),
          icon: "mdi-chart-line",
          color: "#7e04b7",
        },
        {
          key: "current_stock",
          label: this.$t("current_stock"),
          icon: "mdi-package-variant-closed",
          color: "#5578eb",
        },
        {
          key: "reorder_point",
          label: this.$t("reorder_point"),
          icon: "mdi-alert-circle",
          color: "#ff6b35",
        },
        {
          key: "lead_time_days",
          label: this.$t("lead_time_days"),
          icon: "mdi-clock-outline",
          color: "#47be7d",
        },
        {
          key: "last_30d_sales_total_qty",
          label: this.$t("thirty_day_sales"),
          icon: "mdi-cart",
          color: "#5578eb",
        },
        {
          key: "last_60d_sales_total_qty",
          label: this.$t("sixty_day_sales"),
          icon: "mdi-cart-plus",
          color: "#7e04b7",
        },
        {
          key: "last_90d_sales_total_qty",
          label: this.$t("ninety_day_sales"),
          icon: "mdi-cart-arrow-right",
          color: "#47be7d",
        },
      ];

      return metricsMap
        .filter((m) => this.outputData[m.key] !== undefined)
        .map((m) => ({
          ...m,
          value: this.outputData[m.key],
        }));
    },

    /** Confidence level CSS class */
    confidenceClass() {
      if (!this.outputData) return "";
      const level = (this.outputData.confidence || "").toLowerCase();
      return (
        {
          low: "store-report__confidence--low",
          medium: "store-report__confidence--medium",
          high: "store-report__confidence--high",
        }[level] || ""
      );
    },
  },

  beforeMount() {
    this.fetchReportStructure();
  },
  // watch: {
  //   search(val) {
  //     if (!val) return;
  //     this.getData(val);
  //   },
  // },
  methods: {
    /** Return overridden filter title or fall back to API title */
    getFilterTitle(filter) {
      const key = this.filterTitleMap[filter.name];
      if (key && this.$te(key)) {
        return this.$t(key);
      }
      return filter.title;
    },

    /** Display SKU as "code - description" in autocomplete */
    skuItemText(item) {
      if (item.description) {
        return `${item.systemCode} - ${item.description}`;
      }
      return item.systemCode;
    },

    debounceSearch() {
      if (this.search) {
        this.isLoading = true;
        clearTimeout(this.debounce);
        this.debounce = setTimeout(() => {
          this.getData(this.search);
        }, 600);
      }
    },
    async getData(value) {
      this.isLoading = true;
      const payload = { q: value };

      if (this.filterValues?.client) {
        payload.client = this.filterValues.client;
      }
      await fetch(`${process.env.VUE_APP_BASE_URL}/skus/search`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(payload),
      })
        .then((res) => res.json())
        .then((res) => {
          this.entries = res.skus;
        })
        .catch()
        .finally(() => (this.isLoading = false));
    },
    /**
     * Fetch report configuration from API.
     * Populates filters, history, and auto-selects first dropdown values.
     */
    async fetchReportStructure(refresh = false) {
      this.$store.commit(SET_PAGE_LOADING, true);
      try {
        const { data } = await ApiService.post("/reports/reportSkuForecast");
        if (data.success && data.data) {
          this.limit = data.data.limit;
          this.limit_finish = data.data.limit_finish;

          if (refresh) {
            this.reportStructure.history = data.data.history;
          } else {
            this.reportStructure = data.data;

            if (this.isClient) {
              this.reportStructure.filters = data.data.filters.filter(
                (item) => item.name !== "client"
              );
            }
            this.initFilterDefaults();
          }
        }
      } catch (e) {
        // console.error("Failed to fetch report structure:", e);
      } finally {
        this.$store.commit(SET_PAGE_LOADING, false);
      }
    },

    /**
     * Initialize filterValues with first option for selects, empty for inputs.
     */
    initFilterDefaults() {
      const defaults = {};
      this.reportStructure.filters.forEach((filter) => {
        if (
          filter.type === "select2Single" &&
          filter.values &&
          filter.values.length
        ) {
          defaults[filter.name] = filter.values[0].index;
        } else {
          defaults[filter.name] = filter.type === "inputText" ? "" : null;
        }
      });
      this.filterValues = defaults;
    },

    /**
     * Submit filters to API and display result.
     */
    async onAnalyze() {
      this.activeHistoryId = null;

      // Mock response — skip API call, show result immediately
      // this.outputData = {
      //   sku: this.filterValues.sku_forecast || "2222222222222",
      //   last_30d_sales_total_qty: 45,
      //   last_60d_sales_total_qty: 49,
      //   last_90d_sales_total_qty: 55,
      //   current_stock: 0,
      //   lead_time_days: 14,
      //   forecast_30d: 14,
      //   trend: "Flat",
      //   stability: "Volatile",
      //   confidence: "Low",
      //   reorder_point: 10,
      //   justification:
      //     "Last 30d sales were 45 units; the capped 90d average demand is 0.48 units/day. Avg capped sales were 1.00/day in the last 7 days versus 1.13/day in the previous 23 days, so the trend is Flat. Net available is 0 units versus a recommended order quantity of 20, so order needed is 20 units; reorder point is 10.",
      // };

      // this.isLoading = true;
      this.$store.commit(SET_PAGE_LOADING, true);

      const payload = {
        filters: Object.entries(this.filterValues)
          .filter(([, v]) => v !== null && v !== "")
          .map(([name, value]) => ({ name, value })),
      };

      try {
        const { data } = await ApiService.post(
          "/reports/reportSkuForecast/show",
          payload
        );
        if (data && data.success && data.data) {
          this.outputData = data.data;
        }
      } catch (e) {
        // console.error("Failed to fetch forecast data:", e);
      } finally {
        // this.isLoading = false;
        // this.$store.commit(SET_PAGE_LOADING, false);
        await this.fetchReportStructure(true);
      }
    },

    /**
     * Apply a history entry: set filter values + output instantly.
     */
    applyHistory(entry) {
      if (this.activeHistoryId && this.activeHistoryId === entry.id) {
        this.activeHistoryId = null;
        this.outputData = null;
        this.initFilterDefaults();
        return;
      }
      this.activeHistoryId = entry.id;

      // Restore filter values
      const restored = {};
      this.reportStructure.filters.forEach((filter) => {
        const histFilter = entry.filters.find((f) => f.name === filter.name);
        restored[filter.name] = histFilter ? histFilter.value : null;
      });
      this.filterValues = restored;

      // Set output directly from history
      this.outputData = entry.output || null;
    },

    /** Reset all filters and results. */
    resetFilters() {
      this.initFilterDefaults();
      this.outputData = null;
      this.activeHistoryId = null;
    },
  },
};
</script>

<style lang="scss" scoped>
/* ── Variables ──────────────────────────────────────────────── */
$primary: #7e04b7;
$border: rgba(0, 0, 0, 0.06);
$radius: 14px;
$radius-sm: 10px;
$shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 4px 16px rgba(0, 0, 0, 0.04);
$font: "Poppins", sans-serif;

/* ── Page Header ────────────────────────────────────────────── */
.store-report__page-header {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
}

.store-report__page-title {
  font-family: $font;
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 0 8px;
  letter-spacing: -0.3px;
}

/* ── Page wrapper ───────────────────────────────────────────── */
.store-report {
  padding: 0px 32px;

  @media (max-width: 960px) {
    padding: 0px 16px;
    overflow-x: hidden;
  }

  @media (max-height: 1000px) {
    padding: 0px 16px;
    overflow-x: hidden;
  }

  @media (max-width: 600px) {
    padding: 8px 12px;
  }
}

/* ── Fix v-row horizontal overflow on mobile ────────────────── */
::v-deep .v-row {
  @media (max-width: 960px) {
    margin: 0 !important;
    overflow-x: hidden;
    overflow-y: auto;
    max-height: 100vh;
    -webkit-overflow-scrolling: touch;
  }
}

::v-deep .v-col {
  @media (max-width: 960px) {
    padding: 0 !important;
  }
}

/* ── Filter Card ────────────────────────────────────────────── */
.store-report__filter-card {
  background: #ffffff;
  border-radius: $radius;
  box-shadow: $shadow;
  border: 1px solid $border;
  padding: 24px 20px;
  //position: sticky;
  //top: 24px;

  @media (max-width: 960px) {
    padding: 16px 12px;
    margin-bottom: 16px;
    position: static;
  }

  @media (max-height: 800px) {
    padding: 16px 12px;
    margin-bottom: 16px;
    position: static;
  }
}

.store-report__tabs {
  border-bottom: 1px solid $border;
  margin-bottom: 16px;

  ::v-deep .v-tabs-bar {
    height: 48px !important;
  }
}

.store-report__tab {
  font-family: $font;
  font-size: 14px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0.3px;
  color: #64748b !important;

  &.v-tab--active {
    color: $primary !important;
  }
}

.store-report__field {
  margin-bottom: 20px;
}

.store-report__label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: $font;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}

.store-report__label-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.store-report__input ::v-deep .v-input__slot {
  border-radius: $radius-sm !important;
  min-height: 40px !important;
}

.store-report__actions {
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid $border;
}

.store-report__analyze-btn {
  font-family: $font;
  font-weight: 600;
  font-size: 13px;
  letter-spacing: 0.3px;
  border-radius: $radius-sm !important;
  height: 42px !important;
  text-transform: none;
}

/* ── History Section ────────────────────────────────────────── */
.store-report__history-list {
  max-height: 60vh;
  overflow-y: auto;
  padding-inline-end: 4px;

  @media (max-width: 960px) {
    max-height: 300px;
  }

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba($primary, 0.2);
    border-radius: 4px;
  }
}

.store-report__history-item {
  padding: 10px 12px;
  border-radius: $radius-sm;
  border: 1px solid $border;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    background: rgba($primary, 0.04);
    border-color: rgba($primary, 0.15);
  }

  &--active {
    background: rgba($primary, 0.06) !important;
    border-color: rgba($primary, 0.3) !important;
    box-shadow: 0 0 0 1px rgba($primary, 0.1);
  }
}

.store-report__history-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 6px;
}

.store-report__history-chip {
  display: inline-block;
  font-family: $font;
  font-size: 11px;
  font-weight: 500;
  color: $primary;
  background: rgba($primary, 0.08);
  padding: 2px 8px;
  border-radius: 4px;
  line-height: 1.4;
}

.store-report__history-date {
  font-family: $font;
  font-size: 11px;
  color: #94a3b8;
}

/* ── Results Panel ──────────────────────────────────────────── */
.store-report__results {
  padding-inline-start: 24px;
  min-height: 400px;

  @media (max-height: 800px) {
    padding-inline-start: 12px;
  }

  @media (max-width: 960px) {
    padding-inline-start: 0;
    margin-top: 16px;
    margin-bottom: 16px;
  }

  @media (max-width: 600px) {
    padding-inline-start: 0;
    min-height: unset;
  }
}

/* Empty State */
.store-report__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  background: #ffffff;
  border-radius: $radius;
  box-shadow: $shadow;
  border: 1px solid $border;
  text-align: center;
  padding: 48px 24px;

  @media (max-width: 960px) {
    min-height: 300px;
    padding: 32px 16px;
  }

  @media (max-width: 560px) {
    min-height: 100px;
    padding: 24px 12px;
  }
}

.store-report__empty-title {
  font-family: $font;
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 16px 0 6px;
}

.store-report__empty-desc {
  font-family: $font;
  font-size: 13px;
  color: #94a3b8;
  max-width: 300px;
  margin: 0;
  line-height: 1.5;
}

/* Results List */
.store-report__list {
  background: #ffffff;
  border-radius: $radius;
  box-shadow: $shadow;
  border: 1px solid $border;
  overflow-y: hidden;
}

.store-report__list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  border-bottom: 1px solid $border;
  flex-wrap: wrap;
  gap: 8px;

  @media (max-width: 600px) {
    padding: 14px 14px;
  }
}

.store-report__list-title {
  font-family: $font;
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  display: flex;
  align-items: center;
}

.store-report__summary-inner {
  padding: 20px;

  @media (max-width: 600px) {
    padding: 16px 14px;
  }
}

.store-report__meta-row {
  display: flex;
  gap: 24px;
  margin-bottom: 20px;
  padding: 14px 16px;
  background: #f8fafc;
  border-radius: $radius-sm;
  border: 1px solid $border;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 12px;
  }
}

.store-report__meta-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.store-report__meta-label {
  font-family: $font;
  font-size: 13px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.store-report__meta-value {
  font-family: $font;
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
}

/* Confidence levels */
.store-report__confidence {
  &--low {
    color: #fd397a !important;
  }
  &--medium {
    color: #ffb822 !important;
  }
  &--high {
    color: #0abb87 !important;
  }
}

/* Metrics Grid */
.store-report__metrics {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 16px;

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }

  @media (max-width: 385px) {
    grid-template-columns: 1fr;
  }
}

.store-report__metric-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 12px;
  background: #f8fafc;
  border-radius: $radius-sm;
  border: 1px solid $border;
  text-align: center;
  transition: box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    transform: translateY(-1px);
  }
}

.store-report__metric-value {
  font-family: $font;
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin-top: 4px;
}

.store-report__metric-label {
  font-family: $font;
  font-size: 13px;
  font-weight: 500;
  color: #94a3b8;
  margin-top: 2px;
}

/* Justification */
.store-report__justification {
  background: #f8fafc;
  border-radius: $radius-sm;
  border: 1px solid $border;
  padding: 16px;
  margin-bottom: 16px;
}

.store-report__justification-header {
  display: flex;
  align-items: center;
  font-family: $font;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
}

.store-report__justification-text {
  font-family: $font;
  font-size: 15px;
  color: #64748b;
  line-height: 1.6;
  margin: 0;
  font-weight: 500;
}

/* Summary Footer */
.store-report__summary-footer {
  display: flex;
  justify-content: flex-end;
}

.store-report__download-btn {
  font-family: $font;
  font-size: 12px;
  font-weight: 600;
  //border-radius: 8px !important;
  text-transform: none;
  letter-spacing: 0;
}
</style>
